import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Pencil, Trash2, Plus, Loader2, Save, X } from "lucide-react";
import { vendorsApi } from "@/lib/api";
import { useToast } from "@/components/SimpleToast";
import { LastChanged, apiError } from "./vendorUtils";

const emptyOf = (fields) => Object.fromEntries(fields.map((f) => [f.key, ""]));

function ItemForm({ fields, form, setForm, onSave, onCancel, saving, testPrefix }) {
  return (
    <Card className="border-primary/40" data-testid={`${testPrefix}-form`}>
      <CardContent className="pt-5 space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          {fields.map((f) => (
            <div key={f.key} className={`space-y-1.5 ${f.wide ? "sm:col-span-2" : ""}`}>
              <Label>{f.label}{f.required ? " *" : ""}</Label>
              {f.multiline ? (
                <Textarea rows={2} value={form[f.key] || ""} placeholder={f.placeholder} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`${testPrefix}-input-${f.key}`} />
              ) : (
                <Input type={f.type || "text"} value={form[f.key] || ""} placeholder={f.placeholder} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`${testPrefix}-input-${f.key}`} />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} data-testid={`${testPrefix}-cancel`}><X className="w-4 h-4 mr-1.5" />Abbrechen</Button>
          <Button onClick={onSave} disabled={saving} data-testid={`${testPrefix}-save`}>
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}Speichern
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function EditableItemList({ vendorId, kind, items, fields, renderView, onChanged, emptyText, addLabel, isAdmin, canEdit, labels }) {
  const toast = useToast();
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const testPrefix = `vendor-${kind}`;

  const start = (item) => { setEditing(item ? item.id : "new"); setForm(item ? { ...item } : emptyOf(fields)); };

  const save = async () => {
    const missing = fields.find((f) => f.required && !String(form[f.key] || "").trim());
    if (missing) return toast.error(`Bitte „${missing.label}“ ausfüllen`);
    const payload = Object.fromEntries(fields.map((f) => [f.key, form[f.key] || ""]));
    setSaving(true);
    try {
      if (editing === "new") {
        const res = await vendorsApi.addItem(vendorId, kind, payload);
        onChanged([...items, res.data]);
        toast.success(`${labels.one} hinzugefügt`);
      } else {
        const res = await vendorsApi.updateItem(vendorId, kind, editing, payload);
        onChanged(items.map((i) => (i.id === editing ? res.data : i)));
        toast.success(`${labels.one} gespeichert`);
      }
      setEditing(null);
    } catch (e) {
      toast.error(apiError(e, "Speichern fehlgeschlagen"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`${labels.one} „${item[fields[0].key]}“ wirklich löschen?`)) return;
    try {
      await vendorsApi.deleteItem(vendorId, kind, item.id);
      onChanged(items.filter((i) => i.id !== item.id));
      toast.success(`${labels.one} gelöscht`);
    } catch (e) {
      toast.error(apiError(e, "Löschen fehlgeschlagen"));
    }
  };

  const formProps = { fields, form, setForm, onSave: save, onCancel: () => setEditing(null), saving, testPrefix };
  return (
    <div className="space-y-3" data-testid={`${testPrefix}-list`}>
      {canEdit && <div className="flex justify-end">
        <Button onClick={() => start(null)} disabled={editing === "new"} data-testid={`${testPrefix}-add-btn`}><Plus className="w-4 h-4 mr-1.5" />{addLabel}</Button>
      </div>}
      {editing === "new" && <ItemForm {...formProps} />}
      {items.length === 0 && editing !== "new" && <p className="text-sm text-muted-foreground py-8 text-center" data-testid={`${testPrefix}-empty`}>{emptyText}</p>}
      {items.map((item) =>
        editing === item.id ? (
          <ItemForm key={item.id} {...formProps} />
        ) : (
          <Card key={item.id} data-testid={`${testPrefix}-item-${item.id}`}>
            <CardContent className="py-4 flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1 space-y-1">
                {renderView(item)}
                <LastChanged item={item} testId={`${testPrefix}-changed-${item.id}`} className="pt-1" />
              </div>
              <div className="flex gap-1 shrink-0">
                {canEdit && <Button variant="ghost" size="icon" onClick={() => start(item)} title="Bearbeiten" data-testid={`${testPrefix}-edit-${item.id}`}><Pencil className="w-4 h-4" /></Button>}
                {isAdmin && <Button variant="ghost" size="icon" onClick={() => remove(item)} title="Löschen (nur Admin)" data-testid={`${testPrefix}-delete-${item.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>}
              </div>
            </CardContent>
          </Card>
        )
      )}
    </div>
  );
}
